import {
  API_SIMULATION_URL,
  GAS_AND_ELECTRICITY_OFFER_TYPE,
  GAS_OFFER_TYPE,
  isSimple,
  isEnterprise,
} from 'config'
import axios from 'axios'
import PropTypes from 'prop-types'

/** Redux and Kea */
import { kea } from 'kea'
import { put, call } from 'redux-saga/effects'
import { Validations } from 'validations'

import pick from 'lodash/pick'
import omit from 'lodash/omit'
import mapKeys from 'lodash/mapKeys'

import DataLogic from 'containers/App/logic'
import SimpleHabitationLogic from 'containers/SimpleHabitation/logic'
import { csObjectToUnderscore } from 'utils'
import moment from 'moment'

const INVOICING_ENDPOINT = `${API_SIMULATION_URL}/public/simulator/invoicing/`

let HIRED_POWER_ENDPOINT = `${API_SIMULATION_URL}/public/simulator/advanced_hired_power_suggestion/`

if (isSimple) {
  HIRED_POWER_ENDPOINT = `${API_SIMULATION_URL}/public/simulator/simple_hired_power_suggestion/`
}

let fields = {
  invoicing_type: [Validations.isUndefined],
  has_electronic_billing: [Validations.isUndefined],
  has_joint_billing: [Validations.isUndefined],
  ciclo_horario: [Validations.ignoreIf.isGas, Validations.isUndefined],
  electricity_entity: [
    Validations.ignoreIf.isGas,
    Validations.isUndefined,
    Validations.isNull,
  ],
  gas_entity: [
    Validations.ignoreIf.isElectricity,
    Validations.ignoreIf.hasJointBilling,
    Validations.isUndefined,
    Validations.isNull,
  ],
  potencia_contratada: [
    Validations.ignoreIf.isGas,
    Validations.isUndefined,
    Validations.isNull,
  ],
}

if (isSimple) {
  fields.gas_stage = [
    Validations.ignoreIf.isElectricity,
    Validations.isUndefined,
  ]
}

const consumeDefaultValues = {
  consumptionDisaggregation: 's',
  electricityConsumptionsRegistration: [
    {
      startDate: moment()
        .date(1)
        .month(0)
        .format('YYYY-MM-DD'),
      endDate: moment()
        .date(31)
        .month(0)
        .format('YYYY-MM-DD'),
      simples: '',
      svazio: '',
      cheias: '',
      ponta: '',
    },
  ],
  gasConsumptionsRegistration: [
    {
      startDate: moment()
        .date(1)
        .month(0)
        .format('YYYY-MM-DD'),
      endDate: moment()
        .date(31)
        .month(0)
        .format('YYYY-MM-DD'),
      consumption: '',
    },
  ],
}

const consumptionDisaggregations = [
  {
    id: 's',
    name: 'Simples',
    text: 'Simples',
    value: 's',
  },
  {
    id: 'vf',
    name: 'Vazio / Fora de vazio',
    text: 'Vazio / Fora de vazio',
    value: 'vf',
  },
  {
    id: 'vcp',
    name: 'Vazio /  Cheias / Ponta',
    text: 'Vazio /  Cheias / Ponta',
    value: 'vcp',
  },
  {
    id: 'vscp',
    name: 'Vazio / Super Vazio /  Cheias / Ponta',
    text: 'Vazio / Super Vazio /  Cheias / Ponta',
    value: 'vscp',
  },
]

export default kea({
  path: () => ['scenes', 'faturation'],
  connect: {
    actions: [DataLogic, ['setValue', 'setValues', 'setFinished']],
    props: [
      DataLogic,
      ['values', 'offerType', 'translations', 'iframed'],
      SimpleHabitationLogic,
      ['currentEquipments'],
    ],
  },

  actions: () => ({
    fetchFilters: () => ({}),
    setHiredPower: (value) => ({ value }),
    fetchError: (error) => ({ error }),
    fetchHiredPower: () => ({}),
    setFilters: (filters) => ({ filters }),
    setInvalid: (invalid) => ({ invalid }),
    toggleConsumes: () => ({}),
  }),

  reducers: ({ actions }) => ({
    filters: [
      { consumptionDisaggregations },
      PropTypes.any,
      {
        [actions.setFilters]: (_, payload) => ({ ..._, ...payload.filters }),
      },
    ],

    hiredPowerSugested: [
      null,
      PropTypes.any,
      {
        [actions.setHiredPower]: (_, payload) => payload.value,
      },
    ],

    invalid: [
      {},
      PropTypes.object,
      {
        [actions.setInvalid]: (_, payload) => payload.invalid,
      },
    ],

    loading: [
      true,
      PropTypes.bool,
      {
        [actions.fetchFilters]: () => true,
        [actions.setFilters]: () => false,
        [actions.fetchError]: () => false,
      },
    ],

    fields: [fields, PropTypes.object, {}],

    error: [
      false,
      PropTypes.array,
      {
        [actions.fetchFilters]: () => false,
        [actions.setFilters]: () => false,
        [actions.fetchError]: (_, payload) => payload.error,
      },
    ],
  }),

  takeLatest: ({ actions, workers }) => ({
    [actions.fetchFilters]: workers.fetchInvoiceFilters,
    [actions.fetchHiredPower]: workers.fetchHiredPower,
    [actions.toggleConsumes]: workers.toggleConsumes,
  }),

  start: function*() {
    const { fetchFilters, fetchHiredPower } = this.actions
    const offerType = yield DataLogic.get('offerType')

    const { profileFinished } = yield DataLogic.get('finished')
    const settings = yield DataLogic.get('settings')

    // https://sentry2.ubiwhere.com/ubiwhere/simulador-avancado/issues/3364/
    if (!profileFinished) {
      this.props.router.push('/')
    } else {
      // https://gitlab.ubiwhere.com/adene-poupa-energia/adene-simulador-avancado/issues/14 #2
      yield put(fetchFilters())

      if (!settings.isFromProfile) {
        if (offerType.slug !== GAS_OFFER_TYPE && !isEnterprise) {
          yield put(fetchHiredPower())
        }
      }
    }
  },

  workers: {
    *toggleConsumes() {
      const { setValues } = this.actions
      const values = yield DataLogic.get('values')
      const { has_consumes, ciclo_horario } = yield DataLogic.get('values')

      if (has_consumes === 's') {
        if (ciclo_horario === 'b') {
          consumeDefaultValues.consumptionDisaggregation = 'vf'
        }
        yield put(setValues(consumeDefaultValues))
      } else {
        const cleanValues = omit(values, [
          'consumptionDisaggregation',
          'electricityConsumptionsRegistration',
          'gasConsumptionsRegistration',
        ])
        yield put(setValues(cleanValues))
      }
    },

    /**
     * Fetches the data needed for the faturation step
     */
    *fetchInvoiceFilters() {
      const { setFilters, setValues, fetchError } = this.actions
      const values = yield DataLogic.get('values')
      const settings = yield DataLogic.get('settings')

      // Needed params for the Invoicing endpoint
      const params = pick(values, ['offer_type', 'municipality'])

      if (isEnterprise) {
        params['type'] = 'e'
      }

      // Clean response data for frontend use
      const cleanData = (data) => {
        return {
          electricityEntities: data.electricity_entities_serializer,
          gasEntities: data.gas_entities_serializer,
          allEntities: data.all_entities_serializer,
          powerChoices: data.power_choices,
          priceCycleChoices: data.price_list_cycle_choices,
          timeCycleChoices: data.time_cycle_choices,
          defaultValues: { ...data.default_values, ...values } || {},
        }
      }

      try {
        // The invoicing endpoint request
        const response = yield call(axios.get, INVOICING_ENDPOINT, { params })
        const data = cleanData(response.data)
        data.timeCycleChoices && delete data.timeCycleChoices['tetra-horário']
        // Only show entities with Eletricidade and gas
        // https://gitlab.ubiwhere.com/adene-poupa-energia/adene-simulador-avancado/issues/6
        data.allEntities = data.allEntities.filter(({ offer_types }) => {
          return (
            offer_types.find(
              ({ slug }) => slug === GAS_AND_ELECTRICITY_OFFER_TYPE
            ) !== undefined
          )
        })

        // Replace . with , in powerchoices 😑
        data.powerChoices = mapKeys(data.powerChoices, (value, key) =>
          key.replace('.', ',')
        )

        // Default values for this form
        if (!settings.isFromProfile) {
          const defaultValues = {
            invoicing_type: 'atm_ctt',
            has_electronic_billing: true,
            has_joint_billing: false,
            ciclo_horario: data.defaultValues.time_cycle || 's',
            ciclo_tarifa: 'w',
            electricity_entity: null,
            gas_entity: null,
            gas_stage: 1,
          }
          // Set the data to make the component
          yield put(setValues({ ...defaultValues, ...values }))
        }

        yield put(setFilters(data))
      } catch (error) {
        yield put(fetchError(error))
      }
    },

    /**
     * Fetches the Sugested hired power
     */
    *fetchHiredPower() {
      const { setValues, setHiredPower } = this.actions
      const values = yield DataLogic.get('values')
      const currentEquipments = yield SimpleHabitationLogic.get(
        'currentEquipments'
      )

      // Required parameters to the Hired Power endooint
      let params = csObjectToUnderscore(
        pick(values, [
          'simulationCode',
          'municipality',
          'constructionYear',
          'equipmentUtilizationType',
          'equipmentUtilizationProfile',
          // "equipments",
        ])
      )

      // Sum all the habitants
      const { adults, children, elderly } = values
      params.habitants = adults + children + elderly || 0

      // Map equipments for the request (it's very diferent)

      // params.equipment =
      //   params.equipments &&
      //   params.equipments.map(
      //     ({ id, quantity, selectedClass, house_divisions }) => ({
      //       equipment: id,
      //       equipment_profile_consumption: selectedClass && selectedClass.id,
      //       division: house_divisions,
      //       quantity,
      //     })
      //   );

      params.equipment =
        currentEquipments &&
        currentEquipments.map(({ data }) => {
          const { id, quantity, selectedClass, house_divisions } = data
          return {
            equipment: id,
            equipment_profile_consumption: selectedClass && selectedClass.id,
            division: house_divisions,
            quantity,
          }
        })

      // Omit equipments name since API uses single name.
      // params = omit(params, "equipments");

      // Request and set values
      try {
        const response = yield call(axios.post, HIRED_POWER_ENDPOINT, {
          ...params,
        })

        // POTENCIA SUGERIDA DEFAULT 10,35kVA

        const { data } = response
        const { potencia_sugerida } = data

        yield put(setHiredPower(potencia_sugerida))

        yield put(setValues({ potencia_contratada: potencia_sugerida }))
      } catch (error) {}
    },
  },
})



// WEBPACK FOOTER //
// ./src/containers/Faturation/logic.js